// Keep human-facing Matrix labels separate from the stable numeric chargeId.
// Old rows sometimes stored the legacy "<chargeId>-<list item>" binding in
// chargeName; the explicit chargeId field now carries that association.
export const parseLegacyChargeBinding = (value) => {
  const label = String(value ?? "").trim();
  const match = /^(\d+)\s*-\s*(.+)$/.exec(label);
  return match
    ? { chargeId: match[1], subName: match[2].trim() }
    : { chargeId: null, subName: null };
};

const normalizedLabel = (value) => String(value ?? "").trim();

export const getChargeSaveLabel = ({ chargeName, chargeId, listItem } = {}) => {
  const original = normalizedLabel(chargeName);
  const legacy = parseLegacyChargeBinding(original);
  // Only strip a legacy prefix if it is *this charge's* ID. Names like
  // "3-Color Printing" must remain intact when the charge ID is different.
  const base = legacy.chargeId && String(legacy.chargeId) === String(chargeId)
    ? legacy.subName
    : original;
  const child = normalizedLabel(listItem?.ChargeName);
  if (!child || child.toLowerCase() === base.toLowerCase()) return base || child;
  return base ? `${base} — ${child}` : child;
};

/**
 * Repair an unbound legacy row only when the active Matrix has one exact
 * charge-name match. No fuzzy matching and no cross-decorator guessing.
 * A list sub-charge can use the readable "Parent — Option" label.
 * This only identifies the option; callers must explicitly save the binding.
 */
export const findUniqueMatrixChargeByName = ({ chargeName, chargeOptions = [] } = {}) => {
  const normalize = (value) => String(value ?? "").trim().replace(/\s+/g, " ").toLowerCase();
  const wanted = normalize(chargeName);
  if (!wanted) return null;
  const matches = (Array.isArray(chargeOptions) ? chargeOptions : []).filter((option) => {
    if (!option || option.archived || option.disabled) return false;
    const id = Number(option.id);
    if (!Number.isInteger(id) || id <= 0) return false;
    const name = normalize(option.name ?? option.ChargeName);
    return name && (wanted === name || wanted.startsWith(`${name} — `));
  });
  return matches.length === 1 ? matches[0] : null;
};

export const getSelectedListCharge = ({
  charges = [],
  selected,
  savedChargeName,
  chargeId,
  savedChargeId,
  parentChargeName,
} = {}) => {
  const options = Array.isArray(charges) ? charges.filter(
    (item) => item && normalizedLabel(item.ChargeName) &&
      item.Price != null && String(item.Price).trim() !== "" &&
      Number.isFinite(Number(item.Price))
  ) : [];
  if (!options.length) return null;

  // A real current selection must belong to the active Matrix charge.
  if (selected && !Array.isArray(selected) && typeof selected === "object") {
    const matched = options.find((item) =>
      normalizedLabel(item.ChargeName) === normalizedLabel(selected.ChargeName) &&
      String(item.Price) === String(selected.Price)
    );
    if (matched) return matched;
  }

  // Only restore the old sub-item when we are editing the same charge.
  if (savedChargeId != null && String(savedChargeId) === String(chargeId)) {
    const savedName = normalizedLabel(savedChargeName);
    const legacy = parseLegacyChargeBinding(savedName);
    const matched = options.find((item) =>
      savedName === getChargeSaveLabel({
        chargeName: parentChargeName,
        chargeId,
        listItem: item,
      }) ||
      savedName === normalizedLabel(item.ChargeName) ||
      (legacy.chargeId === String(chargeId) &&
        legacy.subName === normalizedLabel(item.ChargeName))
    );
    if (matched) return matched;
  }

  // Maintain the historical first-item default for unspecific list selections.
  return options[0];
};
